import type { TextFieldProps } from '@mui/material/TextField';

import { Controller, useFormContext } from 'react-hook-form';
import { transformValue, transformValueOnBlur, transformValueOnChange } from 'minimal-shared/utils';

import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { Iconify } from '../iconify';

// ----------------------------------------------------------------------

export type RHFTextFieldProps = TextFieldProps & {
  name: string;
  clear?: boolean;
  setSelectedUserAccDtl?: (data: any) => void;
};

export function RHFTextField({
  name,
  helperText,
  slotProps,
  type = 'text',
  clear = false,
  setSelectedUserAccDtl = () => {},
  ...other
}: RHFTextFieldProps) {
  const { control, setValue, getValues } = useFormContext();

  const isNumberType = type === 'number';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          value={isNumberType ? transformValue(field.value) : field.value}
          onChange={(event) => {
            // console.log('🚀 ~ event:', event);
            const newValue = event.target.value.trim();
            if (newValue.length === 0) {
              return field.onChange(''); // Exit early
            }
            const transformedValue = isNumberType
              ? transformValueOnChange(event.target.value)
              : event.target.value;
            field.onChange(transformedValue);
            return '';
          }}
          onBlur={(event) => {
            const newValue = event.target.value.trim();

            if (newValue.length === 0) {
              field.onChange('');
              return;
            }

            const transformedValue = isNumberType ? transformValueOnBlur(newValue) : newValue;

            field.onChange(transformedValue);
          }}
          type={isNumberType ? 'text' : type}
          error={!!error}
          helperText={error?.message ?? helperText}
          sx={{
            '& .MuiInputLabel-root': {
              color: error ? 'error.main' : undefined,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: error ? 'error.main' : undefined,
            },
            '& .MuiInputLabel-root.Mui-disabled': {
              color: error ? 'error.main' : undefined, // force red for disabled label
            },
            '& .MuiFormHelperText-root': {
              color: error ? 'error.main' : undefined,
            },
            '& .MuiOutlinedInput-root': {
              borderColor: error ? 'error.main' : undefined,
              '&.Mui-disabled fieldset': {
                borderColor: error ? 'error.main' : 'divider',
              },
            },
          }}
          InputProps={{
            endAdornment: !!field.value && clear && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setValue(name, ''); // Clear RHF state
                    field.onChange(''); // Clear field value
                    setSelectedUserAccDtl({ value: '', label: '' });
                  }}
                  edge="end"
                  size="small"
                >
                  <Iconify width={16} icon="mingcute:close-line" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          slotProps={{
            ...slotProps,
            htmlInput: {
              autoComplete: 'off',
              ...slotProps?.htmlInput,
              ...(isNumberType && { inputMode: 'decimal', pattern: '[0-9]*.?[0-9]*' }),
            },
          }}
          {...other}
        />
      )}
    />
  );
}
